<template>
  <div id="overlay">
    <div class="image-title">{{ imageTitle }}</div>
    <div class="fullscreen-image">
      <vue-cropper ref="cropper" :src="mealOriginUrl" :alt="imageTitle"
      :data="cropData" :aspectRatio=16/9 :viewMode="1" :checkCrossOrigin="false"
      :movable="false" :ready="setReady" />
    </div>
    <div class="top-right">
      <span @click="close" :title="$t('Close')">
        <i class="fa fa-close fa-fw"></i>
      </span>
    </div>
    <div class="edit-buttons">
      <div class="buttons">
        <button
          class="button"
          @click="rotate(-1)"
          :title="$t('Rotate left')"
          :disabled="!cropperReady"
        >
          <span> <i class="fa fa-undo fa-fw"></i> </span>
          <span class="button-label is-hidden-touch">{{
            $t("Rotate left")
          }}</span>
        </button>
        <button
          class="button"
          @click="rotate(1)"
          :title="$t('Rotate right')"
          :disabled="!cropperReady"
        >
          <span> <i class="fa fa-undo fa-flip-horizontal fa-fw"></i> </span>
          <span class="button-label is-hidden-touch">{{
            $t("Rotate right")
          }}</span>
        </button>
      </div>
    </div>
    <div class="submit-buttons">
      <div class="buttons">
        <button
          class="button is-primary"
          @click="saveCrop"
          :title="$t('Submit')"
          :disabled="!cropperReady"
        >
          <span> <i class="fa fa-check"></i> </span>
          <span class="button-label is-hidden-touch">{{ $t("OK") }}</span>
        </button>
        <button
          class="button"
          @click="reset"
          :disabled="!cropperReady"
          :title="$t('Reset changes')"
        >
          <span> <i class="fa fa-refresh fa-fw"></i> </span>
          <span class="button-label is-hidden-touch">{{
            $t("Reset changes")
          }}</span>
        </button>
        <button class="button is-danger" @click="close" :title="$t('Cancel')">
          <span> <i class="fa fa-times"></i> </span>
          <span class="button-label is-hidden-touch">{{ $t("Cancel") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs"

export default {
  name: "image-editor",
  props: ["imageTitle", "mealOriginUrl", "cropData"],
  components: {
    VueCropper
  },
  data() {
    return {
      cropperReady: false
    }
  },
  watch: {
    cropperReady() {
      this.reset()
    }
  },
  methods: {
    setReady() {
      this.cropperReady = true
    },
    rotate(dir) {
      this.cleanCrop()
      this.$refs.cropper.rotate(dir * 90)
      this.zoomImage()
      this.centerImage()
      this.setDefaultCrop()
    },
    cleanCrop() {
      const container = this.$refs.cropper.getContainerData()
      this.$refs.cropper.setCropBoxData({
        left: container.width / 2,
        top: container.height / 2,
        width: 1,
        height: 1
      })
    },
    zoomImage() {
      const container = this.$refs.cropper.getContainerData()
      const canvas = this.$refs.cropper.getCanvasData()
      const zoom = Math.min(
        container.width / canvas.naturalWidth,
        container.height / canvas.naturalHeight
      )
      this.$refs.cropper.zoomTo(zoom)
    },
    centerImage() {
      const container = this.$refs.cropper.getContainerData()
      const canvas = this.$refs.cropper.getCanvasData()
      this.$refs.cropper.setCanvasData({
        left: (container.width - canvas.width) / 2,
        top: (container.height - canvas.height) / 2
      })
    },
    setDefaultCrop() {
      const canvas = this.$refs.cropper.getCanvasData()
      const aspectRatio = this.$refs.cropper.aspectRatio
      const cropWidth = Math.min(
        canvas.naturalWidth,
        canvas.naturalHeight * aspectRatio
      )
      const cropHeight = Math.min(
        canvas.naturalHeight,
        canvas.naturalWidth / aspectRatio
      )
      this.$refs.cropper.setData({
        x: (canvas.naturalWidth - cropWidth) / 2,
        y: (canvas.naturalHeight - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight
      })
    },
    reset() {
      try {
        this.$refs.cropper.reset()
        if (this.cropData) {
          this.cleanCrop()
          this.zoomImage()
          this.centerImage()
          this.$refs.cropper.setData(this.cropData)
        } else {
          this.setDefaultCrop()
        }
      } catch (e) {
        this.$logger.error("reset-cropper-error", e)
      }
    },
    saveCrop(e) {
      const data = this.$refs.cropper.getData()
      if (data) {
        e.target.editOperations = {
          x: Math.round(data.x),
          y: Math.round(data.y),
          width: Math.round(data.width),
          height: Math.round(data.height),
          rotate: data.rotate
        }
        this.$emit("submit", e)
      }
    },
    close(e) {
      this.$emit("close", e)
    }
  }
}
</script>

<style lang="scss">
#overlay {
  z-index: 9999;
  background-color: #333;
  position: fixed;
  height: 100%;
  width: 100%;
  min-width: 316px;
  max-height: 100%;
  left: 0;
  top: 0;
  text-align: center;
  padding: 2.5rem 0.5rem 4rem 0.5rem;
  color: #fff;
  .edit-buttons {
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
  }
  .submit-buttons {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
  }
  .image-title {
    position: absolute;
    top: 0.25rem;
    left: 0.5rem;
    font-size: 1.25rem;
    font-weight: 400;
  }
  .top-right {
    position: absolute;
    right: 0.25rem;
    top: 0.2rem;
    font-size: 1.25rem;
    cursor: pointer;
  }
  .fullscreen-image {
    height: 100%;
    width: 100%;
    max-height: 100%;
    max-width: 100%;
    overflow: hidden;
    img {
      object-fit: contain;
    }
    .cropper-container {
      height: 100%;
    }
    > div {
      height: 100%;
    }
  }
  .button-label {
    margin-left: 0.25rem;
  }
}
</style>
